import React, { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Label } from "../ui/label"
import { Card, CardContent } from "../ui/card"
import { MessageSquare, Send, User, Phone, Check } from "lucide-react"
import { smsApi } from "../../services/smsApi"
import { attorneysApi } from "../../services/attorneysApi"
import { useMyContext } from "../../context/Context"

interface Attorney {
  name: string
  phone: string
  email: string
}

interface SmsTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  lead: {
    id: string
    name: string
    first_name: string
    phone: string
    assigned_to_attorney?: {
      name: string
      phone: string
      email: string
    } | null
  }
  onSmsSent: (template: string, status: 'success' | 'error', message: string) => void
}

const SMS_TEMPLATES = {
  UNANSWERED: "This is {attorney_name}. I am an employment attorney with Castronovo & McKinney https://www.cmlaw.com. I am returning your call. Please text me or give me a call back at {attorney_phone}. Thanks.\n\nThis is an automated message from Castronovo & McKinney, LLC. Please do not reply to this text, as responses are not monitored. Please reply to {attorney_phone}.",
  CONTACT: "Thank you for taking the time to speak with me. My email is {attorney_email}. You can also find more information on our website www.cmlaw.com. Please do not reply to this text, as responses are not monitored. Please reply to {attorney_phone}."
}

export default function SmsTemplateModal({
  isOpen,
  onClose,
  lead,
  onSmsSent
}: SmsTemplateModalProps) {
  const { user } = useMyContext()
  const [attorneys, setAttorneys] = useState<Attorney[]>([])
  const [loadingAttorneys, setLoadingAttorneys] = useState(false)
  const [selectedAttorney, setSelectedAttorney] = useState<string>("")
  const [selectedTemplate, setSelectedTemplate] = useState<string>("UNANSWERED")
  const [sending, setSending] = useState(false)
  const [previewMessage, setPreviewMessage] = useState("")

  // Fetch attorneys from database when modal opens
  useEffect(() => {
    if (!isOpen) return

    const fetchAttorneys = async () => {
      setLoadingAttorneys(true)
      try {
        const response = await attorneysApi.getAttorneys(1, 10)
        const attorneysList: Attorney[] = response.attorneys
          .filter(attorney => {
            // Only include attorneys who can send SMS
            return attorney.can_send_sms && 
                   attorney.phone && 
                   attorney.email
          })
          .map(attorney => ({
            name: attorney.name,
            phone: attorney.phone,
            email: attorney.email
          }))
        
        setAttorneys(attorneysList)
        
        // Set default selected attorney based on priority:
        // 1. Logged-in user's email matches an attorney
        // 2. First attorney in the list
        if (attorneysList.length > 0) {
          let attorneyToSelect: Attorney | null = null
          
          // Priority 1: Check if logged-in user's email matches any attorney
          if (user?.email) {
            const matchingAttorney = attorneysList.find(a => 
              a.email.toLowerCase() === user.email.toLowerCase()
            )
            if (matchingAttorney) {
              attorneyToSelect = matchingAttorney
            }
          }
          
          // Priority 2: Default to first attorney in the list if user's email didn't match
          if (!attorneyToSelect) {
            attorneyToSelect = attorneysList[0]
          }
          
          setSelectedAttorney(attorneyToSelect.name)
        }
      } catch (error) {
        console.error('Failed to fetch attorneys:', error)
        // Don't call onSmsSent here as it might show an error toast when modal opens
        // The UI will show "No attorneys available" message instead
      } finally {
        setLoadingAttorneys(false)
      }
    }

    fetchAttorneys()
  }, [isOpen, lead.assigned_to_attorney, user?.email])

  // Generate preview message when attorney, template or lead data changes
  useEffect(() => {
    if (!isOpen || !lead || !selectedAttorney || attorneys.length === 0) return

    const attorney = attorneys.find(a => a.name === selectedAttorney)
    if (!attorney) return

    let preview = SMS_TEMPLATES[selectedTemplate as keyof typeof SMS_TEMPLATES]

    // Replace template variables (replace all occurrences)
    if (selectedTemplate === "UNANSWERED") {
      preview = preview
        .replaceAll("{attorney_name}", attorney.name)
        .replaceAll("{attorney_phone}", attorney.phone)
    } else if (selectedTemplate === "CONTACT") {
      preview = preview
        .replaceAll("{attorney_email}", attorney.email)
        .replaceAll("{attorney_phone}", attorney.phone)
    }

    setPreviewMessage(preview)
  }, [selectedAttorney, selectedTemplate, lead, isOpen, attorneys])

  const handleSendSms = async () => {
    if (!lead.phone) {
      onSmsSent(selectedTemplate, 'error', 'No phone number available for this lead')
      return
    }

    setSending(true)
    try {
      const attorney = attorneys.find(a => a.name === selectedAttorney)
      if (!attorney) {
        onSmsSent(selectedTemplate, 'error', 'Selected attorney not found')
        return
      }

      const result = await smsApi.sendSms({
        lead_id: lead.id,
        message_body: previewMessage
      })

      if (result.error === false) {
        onSmsSent(selectedTemplate, 'success', `SMS sent successfully to ${lead.name}`)
        onClose()
      } else {
        onSmsSent(selectedTemplate, 'error', result.message || "Failed to send SMS")
      }
    } catch (error) {
      console.error('Failed to send SMS:', error)
      onSmsSent(selectedTemplate, 'error', "Failed to send SMS. Please try again.")
    } finally {
      setSending(false)
    }
  }

  const handleClose = () => {
    setSelectedAttorney("")
    setSelectedTemplate("UNANSWERED")
    setSending(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-sm mx-auto transition-none p-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send SMS to {lead.name}
          </DialogTitle>
          <DialogDescription>
            Choose an attorney and SMS template to send to this lead
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2">
          {/* Attorney Selection */}
          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Select Attorney</Label>
            {loadingAttorneys ? (
              <div className="p-2 text-sm text-slate-500">Loading attorneys...</div>
            ) : attorneys.length === 0 ? (
              <div className="p-2 text-sm text-red-500">No attorneys available</div>
            ) : (
              <div className="space-y-1">
                {attorneys.map((attorney) => (
                <div
                  key={attorney.name}
                  className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                    selectedAttorney === attorney.name
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedAttorney(attorney.name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 text-sm">{attorney.name}</div>
                    </div>
                    {selectedAttorney === attorney.name && (
                      <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>

          {/* Template Selection */}
          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Select Template</Label>
            <div className="space-y-1">
              <div
                className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                  selectedTemplate === "UNANSWERED"
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedTemplate("UNANSWERED")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 text-sm">Return Call</div>
                    <div className="text-xs text-slate-600">Return missed call</div>
                  </div>
                  {selectedTemplate === "UNANSWERED" && (
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </div>
              <div
                className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                  selectedTemplate === "CONTACT"
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedTemplate("CONTACT")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 text-sm">Follow-up Contact</div>
                    <div className="text-xs text-slate-600">Provide contact info</div>
                  </div>
                  {selectedTemplate === "CONTACT" && (
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Message Preview */}
          <div className="space-y-1">
            <Label className="text-sm font-medium text-slate-700">Message Preview</Label>
            <Card className="border-slate-200">
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 leading-relaxed">
                      {previewMessage}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>To: {lead.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        <span>{lead.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Attorney Information */}
        </div>

        <DialogFooter className="flex gap-2 pt-2">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendSms}
            disabled={sending || !lead.phone || loadingAttorneys || attorneys.length === 0 || !selectedAttorney}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
